import { faLink, faMapSigns, faPhotoFilm } from '@fortawesome/free-solid-svg-icons'
import {
  AcknowledgmentQuestionIcon,
  AssetFieldsQuestionIcon,
  AssetMoudleIcon,
  AssignmentsModuleIcon,
  AudioQuestionIcon,
  BarcodeQuestionIcon,
  CalculationQuestionIcon,
  CheckboxQuestionIcon,
  ClockQuestionIcon,
  ConstantQuestionIcon,
  DatasetQuestionIcon,
  DateQuestionIcon,
  DocumentIdQuestionIcon,
  DropdownQuestionIcon,
  GlobalListQuestionIcon,
  InstructionQuestionIcon,
  IssueModuleIcon,
  ListQuestionIcon,
  LocationQuestionIcon,
  LongTextQuestionIcon,
  NumberQuestionIcon,
  PhotoQuestionIcon,
  QRQuestionIcon,
  RepeatIcon,
  SignatureQuestionIcon,
  SliderQuestionIcon,
  SparePartsQuestionIcon,
  SwitchQuestionIcon,
  TextIcon,
  TextQuestionIcon,
  UserIcon,
  VideoQuestionIcon,
  YesNoQuestionIcon
} from '@csrc/assets/icons'


import { ServerItemType } from "@cmodules/checklist/assets/data/types/vm/template-builder/server-item-type"

export interface OptionDefinition {
  type: string
  auto_generate?: boolean
  default_value: any
  required?: boolean
}

export interface TemplateItemDefinition {
  type: ServerItemType;

  /** Optional subtype of the item */
  subType?: string;

  /** Group identifier for this item */
  group?: string[];

  /** Indicates whether this item can have children */
  hasChild: boolean;

  /** Determines if the item can be dragged in the UI */
  canDrag: boolean;

  /** Background color for the item */
  background: string;

  /** Optional background color for the icon */
  iconBackground?: string;

  /** Icon configuration */
  icon?: {
    /** React/Vue/etc. component for the icon */
    component: any;
    /** Icon color */
    color: string;
  };

  /** If false, the item type will be disabled in selecting item type modal (item is disabled by-default) */
  enable?: boolean;

  /** If true, the item type will be disabled in selecting item type modal when the template is global (item is enabled in global templates by-default) */
  disableItemInGlobalTemplates?: boolean;

  /** Still available for edit but removed from the list */
  deprecated?: boolean;

  /** If true, the item is hidden from the UI in selecting item type modal */
  hidden?: boolean;

  /** Defines the behavior type */
  behaviourType?: string;

  /** Title displayed in the UI */
  title?: string;

  /** Prevents adding duplicate items of this type on the same page */
  preventDuplicateInPage?: boolean;

  /** Marks this item as usable as a dependency */
  asDependency?: boolean;

  /** Marks this item as not valid for import */
  notValidForImport?: boolean;

  /** Feature flag key controlling availability */
  featureKey?: string;

  /** IDs of questions to exclude from this item */
  excludedQuestions?: string[];

  /** Rules and actions flags */
  rulesActions?: Record<string, boolean>;

  /** Configuration options for the item */
  options: Record<string, OptionDefinition>;
}

export type TemplateItemsT = Record<ServerItemType, TemplateItemDefinition>

export const GlobalTypesWithoutSingleItem: ServerItemType[] = ['global_page', 'global_start_page', 'global_end_page', 'global_section', 'global_items']

export const TemplateItems: TemplateItemsT = {
  page: {
    type: "page",
    subType: 'main',
    behaviourType: 'page',
    title: "page",
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      image_marker_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      not_visible: {
        type: "bool",
        default_value: false
      }
    },
    excludedQuestions: [
      'person',
      'site',
      'asset',
      'document_id',
      'inspection_location',
      'inspection_real_start_date',
      'inspection_start_date',
      'inspection_end_date',
      "inspection_real_end_date",
      "file_manager",
      "end_signature",
      "url"
    ],
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  start_page: {
    type: "start_page",
    subType: 'start',
    behaviourType: 'page',
    title: "startPage",
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      not_visible: {
        type: "bool",
        default_value: false
      }
    },
    excludedQuestions: [
      "inspection_end_date",
      "inspection_real_end_date",
      "file_manager",
      "end_signature",
      "url"
    ]
  },
  global_start_page: {
    type: "global_start_page",
    subType: 'start',
    behaviourType: 'page',
    title: "startPage",
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      not_visible: {
        type: "bool",
        default_value: false
      }
    },
    excludedQuestions: [
      "inspection_end_date",
      "inspection_real_end_date",
      "file_manager",
      "end_signature",
      "url"
    ]
  },
  end_page: {
    type: "end_page",
    subType: 'end',
    behaviourType: 'page',
    title: "endPage",
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      not_visible: {
        type: "bool",
        default_value: false
      }
    },
    excludedQuestions: [
      'site',
      // 'person',
      'asset',
      'document_id',
      'inspection_location',
      'inspection_real_start_date',
      'inspection_start_date',
      "file_manager",
      "url"
    ]
  },
  global_end_page: {
    type: "global_end_page",
    subType: 'end',
    behaviourType: 'page',
    title: "endPage",
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      not_visible: {
        type: "bool",
        default_value: false
      }
    },
    excludedQuestions: [
      'site',
      // 'person',
      'asset',
      'document_id',
      'inspection_location',
      'inspection_real_start_date',
      'inspection_start_date',
      "file_manager",
      "url"
    ]
  },
  hidden_page: {
    type: "hidden_page",
    subType: 'hidden',
    behaviourType: 'page',
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      not_visible: {
        type: "bool",
        default_value: true
      }
    },
    excludedQuestions: []
  },
  section: {
    type: "section",
    hasChild: true,
    canDrag: true,
    background: '#f5f5f5',
    options: {
      grouped_item_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      image_marker_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      repeatable: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  inspection_number: {
    type: "inspection_number",
    group: ["startPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F21F',
    icon: {
      component: TextIcon,
      color: "#35434D"
    },
    enable: true,
    deprecated: true,
    preventDuplicateInPage: true,
    asDependency: false,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    }
  },
  asset: {
    type: "asset",
    group: ["startPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F233',
    icon: {
      color: '#35434D',
      component: AssetMoudleIcon
    },
    enable: true,
    preventDuplicateInPage: true,
    asDependency: true,
    behaviourType: "text",
    notValidForImport: true,
    options: {
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  document_id: {
    type: "document_id",
    group: ["startPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F233',
    icon: {
      color: '#35434D',
      component: DocumentIdQuestionIcon
    },
    enable: true,
    deprecated: false,
    preventDuplicateInPage: true,
    asDependency: true,
    behaviourType: "text",
    notValidForImport: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      format: {
        type: "string",
        default_value: "#{number}"
      },
      items: {
        type: "array",
        default_value: [{ key: 'number', type: 'unique' }]
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  inspection_start_date: {
    type: "inspection_start_date",
    group: ["startPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F233',
    icon: {
      color: '#35434D',
      component: ClockQuestionIcon
    },
    enable: true,
    preventDuplicateInPage: true,
    asDependency: false,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      date: {
        type: "bool",
        default_value: true
      },
      date_time_shape: {
        type: "string",
        default_value: "year_month_day_hour_minute"
      },
      default_value: {
        type: "object",
        default_value: {
          period_type: "",
          type: "current"
        }
      },
      enable_rating: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      future_date: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      prior_date: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      required: {
        type: "bool",
        default_value: false
      },
      time: {
        type: "bool",
        default_value: true
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  inspection_location: {
    type: "inspection_location",
    group: ["startPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F233',
    icon: {
      color: '#35434D',
      component: LocationQuestionIcon
    },
    enable: true,
    preventDuplicateInPage: true,
    asDependency: false,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  person: {
    type: "person",
    group: ["startPage", "endPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F233',
    icon: {
      color: '#35434D',
      component: UserIcon
    },
    enable: true,
    preventDuplicateInPage: false,
    asDependency: false,
    options: {
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  inspection_real_start_date: {
    type: "inspection_real_start_date",
    group: ["startPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F21F',
    icon: {
      color: '#35434D',
      component: TextIcon
    },
    enable: false,
    hidden: true,
    preventDuplicateInPage: true,
    asDependency: false,
    options: {
      required: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  file_manager: {
    type: "file_manager",
    group: ["startPage"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: 'rgba(0,50,255,0.12)',
    icon: {
      color: '#35434D',
      component: faPhotoFilm
    },
    enable: true,
    notValidForImport: true,
    options: {},
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  url: {
    type: "url",
    group: ["startPage"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: 'rgba(0,0,0,0.12)',
    icon: {
      color: '#35434D',
      component: faLink
    },
    enable: true,
    options: {},
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  inspection_real_end_date: {
    type: "inspection_real_end_date",
    group: ["endPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#ff9f431F',
    icon: {
      color: '#35434D',
      component: TextIcon
    },
    enable: false,
    hidden: true,
    preventDuplicateInPage: true,
    asDependency: false,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  inspection_end_date: {
    type: "inspection_end_date",
    group: ["endPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#1477F233',
    icon: {
      color: '#35434D',
      component: ClockQuestionIcon
    },
    enable: true,
    preventDuplicateInPage: true,
    asDependency: false,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      date: {
        type: "bool",
        default_value: true
      },
      date_time_shape: {
        type: "string",
        default_value: "year_month_day_hour_minute"
      },
      default_value: {
        type: "object",
        default_value: {
          period_type: "",
          type: "current"
        }
      },
      enable_rating: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      future_date: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      prior_date: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      required: {
        type: "bool",
        default_value: false
      },
      time: {
        type: "bool",
        default_value: true
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  textAnswer: {
    type: "textAnswer",
    group: ["text"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#EBBE4C33',
    icon: {
      color: '#35434D',
      component: TextQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "text",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      default_value: {
        type: 'string',
        default_value: ""
      },
      enable_rating: {
        type: 'bool',
        default_value: false
      },
      enable_score: {
        type: 'bool',
        default_value: false
      },
      exclude_report: {
        type: 'bool',
        default_value: false
      },
      hidden: {
        type: 'bool',
        default_value: false
      },
      max_length: {
        type: 'string',
        default_value: ""
      },
      min_length: {
        type: 'string',
        default_value: ""
      },
      read_only: {
        type: 'bool',
        default_value: false
      },
      require_media: {
        type: 'bool',
        default_value: false
      },
      require_note: {
        type: 'bool',
        default_value: false
      },
      required: {
        type: 'bool',
        default_value: false
      },
      secure: {
        type: 'bool',
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  longText: {
    type: "longText",
    group: ["text"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#EBBE4C33',
    icon: {
      color: '#35434D',
      component: LongTextQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "text",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      min: {
        type: "string",
        default_value: ""
      },
      max: {
        type: "string",
        default_value: ""
      },
      enable_score: {
        type: 'bool',
        default_value: false
      },
      default_value: {
        type: 'string',
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  instruction: {
    type: "instruction",
    group: ["text"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#EBBE4C33',
    icon: {
      color: '#35434D',
      component: InstructionQuestionIcon
    },
    enable: true,
    behaviourType: "text",
    title: "checklist.templateBuilder.title",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  site: {
    type: "site",
    group: ["text"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#ff9f431F',
    icon: {
      color: '#35434D',
      component: faMapSigns
    },
    enable: true,
    deprecated: true,
    preventDuplicateInPage: false,
    asDependency: false,
    options: {
      default_value: {
        type: 'string',
        default_value: ""
      },
      editable: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      required: {
        type: "bool",
        default_value: false
      },
      column_options: {
        type: "object",
        default_value: {
          column_id: null,
          structure_id: null,
          structure_node_id: null
        }
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  number: {
    type: "number",
    group: ["numeric"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#50AD6A33',
    icon: {
      color: '#35434D',
      component: NumberQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "number",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      secure: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      grouping_separator: {
        type: "bool",
        default_value: false
      },
      decimal: {
        type: "number",
        default_value: ""
      },
      min: {
        type: "number",
        default_value: ""
      },
      max: {
        type: "number",
        default_value: ""
      },
      default_value: {
        type: "number",
        default_value: ""
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      enable_rating: {
        type: "bool",
        default_value: false
      },
      units: {
        type: "array",
        default_value: []
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  slider: {
    type: "slider",
    group: ["numeric"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#50AD6A33',
    icon: {
      color: '#35434D',
      component: SliderQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "number",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      grouping_separator: {
        type: "bool",
        default_value: false
      },
      min: {
        required: true,
        type: "number",
        default_value: 0
      },
      max: {
        required: true,
        type: "number",
        default_value: 10
      },
      step: {
        required: true,
        type: "number",
        default_value: 1
      },
      default_value: {
        type: "number",
        default_value: ""
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      enable_rating: {
        type: "bool",
        default_value: false
      },
      units: {
        type: "array",
        default_value: []
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  constant: {
    type: "constant",
    group: ["numeric"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#50AD6A33',
    icon: {
      color: '#35434D',
      component: ConstantQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "number",
    title: "checklist.inputs.label",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      grouping_separator: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: true
      },
      default_value: {
        required: true,
        type: "string",
        default_value: ""
      },
      unit_type: {
        type: "string",
        default_value: null
      },
      units: {
        type: "array",
        default_value: []
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  calculation: {
    type: "calculation",
    group: ["numeric"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#50AD6A33',
    icon: {
      color: '#35434D',
      component: CalculationQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "number",
    title: "checklist.inputs.label",
    notValidForImport: true,
    featureKey: 'ckl_global_list',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      editable: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      grouping_separator: {
        type: "bool",
        default_value: false
      },
      calculations: {
        type: "object",
        default_value: {
          value: "",
          items: []
        }
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      enable_rating: {
        type: "bool",
        default_value: false
      },
      unit_type: {
        type: "string",
        default_value: null
      },
      units: {
        type: "array",
        default_value: []
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  issues_count: {
    type: "issues_count",
    group: ["numeric"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#50AD6A33',
    icon: {
      color: '#35434D',
      component: IssueModuleIcon
    },
    enable: true,
    deprecated: true,
    asDependency: true,
    behaviourType: "number",
    notValidForImport: true,
    featureKey: 'act_create_issue',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      scope: {
        type: "string",
        default_value: "all"
      },
      categories: {
        type: "array",
        required: true,
        default_value: []
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  counter: {
    type: "counter",
    group: ["numeric"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#50AD6A33',
    icon: {
      color: '#35434D',
      component: IssueModuleIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "number",
    notValidForImport: true,
    // deprecated: isProduction(),
    // featureKey: 'act_create_issue',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      scope: {
        type: "string",
        default_value: "all"
      },
      type: {
        type: "string",
        required: true,
        default_value: 'yesno'
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  datetime: {
    type: "datetime",
    group: ["datetime"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#A0A0A033',
    icon: {
      color: '#35434D',
      component: DateQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "datetime",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      date: {
        type: "bool",
        default_value: true
      },
      date_time_shape: {
        type: "string",
        default_value: "year_month_day_hour_minute"
      },
      default_value: {
        type: "object",
        default_value: {
          period_type: "",
          type: "current"
        }
      },
      enable_rating: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      future_date: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      prior_date: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      required: {
        type: "bool",
        default_value: false
      },
      time: {
        type: "bool",
        default_value: true
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  yesno: {
    type: "yesno",
    group: ["yesno"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#8947DE33',
    icon: {
      color: '#35434D',
      component: YesNoQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "list",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      response_set_id: {
        type: "uuid",
        default_value: ""
      },
      default_value: {
        type: "uuid",
        default_value: ""
      },
      options_type: {
        type: "uuid",
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  switch: {
    type: "switch",
    group: ["yesno"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#8947DE33',
    icon: {
      color: '#35434D',
      component: SwitchQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "list",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      response_set_id: {
        type: "uuid",
        default_value: ""
      },
      default_value: {
        type: "uuid",
        default_value: ""
      },
      options_type: {
        type: "uuid",
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  checkbox: {
    type: "checkbox",
    group: ["yesno"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#8947DE33',
    icon: {
      color: '#35434D',
      component: CheckboxQuestionIcon
    },
    enable: true,
    deprecated: false,
    asDependency: true,
    options: {
      required: {
        type: "bool",
        default_value: false
      },
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      default_value: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  list: {
    type: "list",
    group: ["select"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#D9813033',
    icon: {
      color: '#35434D',
      component: ListQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "list",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      multiple_selection: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      response_set_id: {
        type: "uuid",
        default_value: ""
      },
      default_value: {
        type: "uuid",
        default_value: ""
      },
      options_type: {
        type: "uuid",
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  dropdown: {
    type: "dropdown",
    group: ["select"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#D9813033',
    icon: {
      color: '#35434D',
      component: DropdownQuestionIcon
    },
    enable: true,
    asDependency: true,
    behaviourType: "list",
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      read_only: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      require_media: {
        type: "bool",
        default_value: false
      },
      require_note: {
        type: "bool",
        default_value: false
      },
      multiple_selection: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      response_set_id: {
        type: "uuid",
        default_value: ""
      },
      default_value: {
        type: "uuid",
        default_value: ""
      },
      options_type: {
        type: "uuid",
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  global_list: {
    type: "global_list",
    group: ["select"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#D9813033',
    icon: {
      color: '#35434D',
      component: GlobalListQuestionIcon
    },
    enable: true,
    deprecated: false,
    preventDuplicateInPage: false,
    asDependency: true,
    featureKey: 'ckl_global_list',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      enable_score: {
        type: "bool",
        default_value: false
      },
      advanced: {
        type: "bool",
        default_value: false
      },
      global_list_uuid: {
        type: "uuid",
        required: true,
        default_value: ""
      },
      separator: {
        type: "string",
        default_value: "/"
      },
      label_field_keys: {
        type: "array",
        default_value: [
          "item_id",
          "display_name"
        ]
      },
      value_field_keys: {
        type: "array",
        default_value: ["item_id"]
      },
      field_type: {
        type: "string",
        default_value: "text"
      },
      value_type: {
        type: "string",
        default_value: "text"
      },
      global_list_id: {
        type: "number",
        required: true,
        default_value: 0
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  dataset: {
    type: "dataset",
    group: ["select"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#00cfe81F',
    icon: {
      color: '#35434D',
      component: DatasetQuestionIcon
    },
    enable: true,
    deprecated: true,
    asDependency: true,
    behaviourType: "text",
    options: {
      required: {
        type: "bool",
        default_value: false
      },
      sites: {
        type: "array",
        default_value: []
      },
      info_id: {
        type: "string",
        default_value: "uuid"
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  spare_parts: {
    type: "spare_parts",
    group: ["select"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: 'rgba(255,0,0,0.12)',
    icon: {
      color: '#35434D',
      component: SparePartsQuestionIcon
    },
    enable: true,
    deprecated: true,
    asDependency: true,
    options: {
      required: {
        type: "bool",
        default_value: false
      },
      sites: {
        type: "array",
        default_value: []
      },
      info_id: {
        type: "string",
        default_value: "uuid"
      },
      quantity: {
        type: 'number',
        default_value: 0
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  signature: {
    type: "signature",
    group: ["approval"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#DD363633',
    icon: {
      color: '#35434D',
      component: SignatureQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  acknowledgment: {
    type: "acknowledgment",
    group: ["approval"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#DD363633',
    icon: {
      color: '#35434D',
      component: AcknowledgmentQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  photo: {
    type: "photo",
    group: ["multimedia"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#35434D33',
    icon: {
      color: '#35434D',
      component: PhotoQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      multi_photo: {
        type: "bool",
        default_value: false
      },
      allow_camera: {
        type: "bool",
        default_value: false
      },
      photo_library: {
        type: "bool",
        default_value: false
      },
      allow_gps_tagging: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      quality: {
        type: "string",
        default_value: "normal"
      },
      default_value: {
        type: "string",
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  video: {
    type: "video",
    group: ["multimedia"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#35434D33',
    icon: {
      color: '#35434D',
      component: VideoQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      allow_camera: {
        type: "bool",
        default_value: false
      },
      photo_library: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      quality: {
        type: "string",
        default_value: "normal"
      },
      max_length: {
        type: "number",
        default_value: 20
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  audio: {
    type: "audio",
    group: ["multimedia"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#35434D33',
    icon: {
      color: '#35434D',
      component: AudioQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      quality: {
        type: "string",
        default_value: "normal"
      },
      max_length: {
        type: "number",
        default_value: 20
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  qr: {
    type: "qr",
    group: ["scanning"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#BE7B2C33',
    icon: {
      color: '#35434D',
      component: QRQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      populate_answers: {
        type: "bool",
        default_value: false
      },
      answer_mappings: {
        type: "array",
        default_value: []
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  barcode: {
    type: "barcode",
    group: ["scanning"],
    hasChild: true,
    canDrag: true,
    background: '#fff',
    iconBackground: '#BE7B2C33',
    icon: {
      color: '#35434D',
      component: BarcodeQuestionIcon
    },
    enable: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  end_signature: {
    type: "end_signature",
    group: ["endPage"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#DD363633',
    icon: {
      color: '#35434D',
      component: SignatureQuestionIcon
    },
    enable: true,
    deprecated: true,
    preventDuplicateInPage: true,
    asDependency: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  repeating_group: {
    type: "repeating_group",
    group: ["endPage"],
    hasChild: false,
    canDrag: true,
    background: '#f5f5f5',
    iconBackground: '#006ed533',
    icon: {
      color: '#35434D',
      component: RepeatIcon
    },
    enable: false,
    deprecated: true,
    preventDuplicateInPage: false,
    asDependency: false,
    notValidForImport: true,
    options: {
      required: {
        type: "bool",
        default_value: false
      },
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  asset_field: {
    type: "asset_field",
    group: ["misc"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#EBBE4C33',
    icon: {
      color: '#35434D',
      component: AssetFieldsQuestionIcon
    },
    enable: true,
    preventDuplicateInPage: false,
    asDependency: true,
    notValidForImport: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      editable: {
        type: "bool",
        default_value: false
      },
      asset_type_uuid: {
        type: "uuid",
        required: true,
        default_value: ""
      },
      field_key: {
        type: "string",
        required: true,
        default_value: ""
      },
      field_type: {
        type: "string",
        required: true,
        default_value: ""
      },
      value_type: {
        type: "string",
        required: true,
        default_value: ""
      },
      asset_type_id: {
        type: "number",
        required: true,
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  assignment_field: {
    type: "assignment_field",
    group: ["misc"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#EBBE4C33',
    icon: {
      color: '#35434D',
      component: AssignmentsModuleIcon
    },
    // deprecated: isProduction(),
    behaviourType: 'text',
    enable: true,
    preventDuplicateInPage: false,
    asDependency: true,
    notValidForImport: true,
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      required: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      editable: {
        type: "bool",
        default_value: false
      },
      asset_type_uuid: {
        type: "uuid",
        required: true,
        default_value: ""
      },
      field_key: {
        type: "string",
        required: true,
        default_value: ""
      },
      field_type: {
        type: "string",
        required: true,
        default_value: ""
      },
      value_type: {
        type: "string",
        required: true,
        default_value: ""
      },
      asset_type_id: {
        type: "number",
        required: true,
        default_value: ""
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  global_item: {
    type: "global_item",
    group: ["misc"],
    hasChild: false,
    canDrag: true,
    background: '#fff',
    iconBackground: '#EBBE4C33',
    icon: {
      color: '#35434D',
      component: AssetFieldsQuestionIcon
    },
    hidden: true,
    // shortcutActions: GlobalItem,
    preventDuplicateInPage: false,
    asDependency: true,
    notValidForImport: true,
    options: {

    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  global_items: {
    type: "global_items",
    group: ["misc"],
    hasChild: true,
    canDrag: true,
    background: '#f5f5f5',
    icon: {
      color: '#35434D',
      component: AssetFieldsQuestionIcon
    },
    hidden: true,
    preventDuplicateInPage: false,
    asDependency: false,
    notValidForImport: true,
    options: {
      grouped_item_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      image_marker_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      repeatable: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: true
    }
  },
  global_page: {
    type: "page",
    subType: 'main',
    behaviourType: 'page',
    title: "global_page",
    hasChild: true,
    canDrag: false,
    background: '#fff',
    options: {
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      image_marker_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      hidden: {
        type: "bool",
        default_value: false
      },
      not_visible: {
        type: "bool",
        default_value: false
      }
    },
    excludedQuestions: [
      'person',
      'site',
      'asset',
      'document_id',
      'inspection_location',
      'inspection_real_start_date',
      'inspection_start_date',
      'inspection_end_date',
      "inspection_real_end_date",
      "file_manager",
      "end_signature",
      "url"
    ],
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  },
  global_section: {
    type: "global_section",
    hasChild: true,
    canDrag: true,
    background: '#f5f5f5',
    // shortcutActions: Section,
    options: {
      grouped_item_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      info_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      image_marker_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      rule_set_id: {
        type: 'uuid',
        auto_generate: false,
        default_value: ""
      },
      exclude_report: {
        type: "bool",
        default_value: false
      },
      repeatable: {
        type: "bool",
        default_value: false
      },
      hidden: {
        type: "bool",
        default_value: false
      }
    },
    rulesActions: {
      visibility: true,
      rule: true,
      preActions: false
    }
  }
} as const

